import { useQuery } from "@tanstack/react-query";
import { NavFilterTile } from "../../core/blocks/NavFilterTile";
import { getOrganizationsOptions } from "../../../modules/admin/api/useOrganizations";
import { useSetOrgId } from "../../../modules/admin/api/useSetCurrentOrg";
import { useAppSelector } from "../../../redux/hooks";
import { useTranslation } from "react-i18next";
import { KDP_AUTHOR } from "../../../utils/profileSubtypes";

const SellerAccount = ({ mobileSidebarItem }: { mobileSidebarItem?: boolean }) => {
  const { t } = useTranslation(["common"]);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const user = useAppSelector((state) => state.user);
  const displayOrganizationName = useAppSelector((state) => state.user.prefers_org_name_display);
  const setOrgMutation = useSetOrgId();

  const orgsQuery = useQuery({
    ...getOrganizationsOptions({ channel }),
    select: (data) => {
      const orgOptions = [];
      const profileNames: Record<string, number> = {};

      const dropdownLabelAccessor = displayOrganizationName ? "name" : "profile_name";

      data.forEach((org) => {
        if (!profileNames[org[dropdownLabelAccessor]]) {
          profileNames[org[dropdownLabelAccessor]] = 1;
        } else {
          profileNames[org[dropdownLabelAccessor]]! += 1;
        }
      });
      const grouped = Object.groupBy(data, (item) =>
        item.profile_subtype === KDP_AUTHOR ? "kdp" : (item.profile_type ?? "other"),
      );

      for (const profile_type in grouped) {
        const group_label = () => {
          switch (profile_type) {
            case "vendor":
              return t(($) => $.common.navbar["Vendors"]);
            case "seller":
              return t(($) => $.common.navbar["Sellers"]);
            case "kdp":
              return t(($) => $.common.navbar["KDP Authors"]);
            default:
              return t(($) => $.common.navbar["Other"]);
          }
        };

        orgOptions.push({ header: true as const, label: group_label() });
        for (const org of grouped[profile_type]!) {
          let profile_name = `${org[dropdownLabelAccessor]}`;
          if (profileNames[profile_name]! > 1 || mobileSidebarItem) {
            profile_name = `${profile_name} (${profile_type})`;
          }

          orgOptions.push({
            value: org.id.toString(),
            label: profile_name,
          });
        }
      }

      return orgOptions;
    },
  });

  return (
    <NavFilterTile
      isLoading={setOrgMutation.isPending || orgsQuery.isLoading}
      selectedKey={user.organization_id}
      items={
        orgsQuery.data ?? [
          {
            value: user.organization_id,
            label: displayOrganizationName ? user.organization_name : user.current_profile,
          },
        ]
      }
      onSelectionChange={(id: string) => {
        const num_id = parseInt(id);
        setOrgMutation.mutate(num_id);
      }}
      mobileSidebarItem={mobileSidebarItem}
    />
  );
};

export default SellerAccount;
